/**
 * Kho dữ liệu Tự động hoá v2 — đồng bộ với máy chủ (/api/v1/automation-v2).
 * Giữ nguyên giao diện useStoreV2() / storeV2 như bản demo để các màn không đổi,
 * nhưng mọi thao tác giờ ghi vào CSDL theo tổ chức; luồng đang bật chạy thật ở
 * máy chủ (engine nền), nhật ký lấy từ máy chủ.
 * Lần đầu tổ chức chưa có gì: tự nạp các luồng mẫu (đang TẮT) + bảng ánh xạ mặc định.
 */
import { useEffect, useSyncExternalStore } from 'react'
import { toast } from 'sonner'
import { api, apiError } from '@/lib/api-client'
import type { Connector, Mapping, WorkflowRun, WorkflowV2 } from '@/lib/automation-v2/types'
import { KIOTVIET_DEFAULT_MAPPING } from '@/lib/automation-v2/kiotviet'
import { MAU_WORKFLOW, luongTuMau, type MauWorkflow } from '@/lib/automation-v2/mau-workflow'

export interface StoreV2 {
  workflows: WorkflowV2[]
  connectors: Connector[]
  mappings: Mapping[]
  runs: WorkflowRun[]
  /** Đã tải lần đầu từ máy chủ chưa. */
  daTai: boolean
  dangTai: boolean
}

/** Luồng có sẵn khi mở lần đầu = một phần thư viện mẫu (xem mau-workflow.ts). */
const LUONG_CO_SAN = ['wf_cham_soc_30_ngay', 'wf_ket_ban_truoc', 'wf_khang_sinh', 'wf_len_hang', 'wf_khieu_nai', 'wf_sinh_nhat', 'wf_cho_lau', 'wf_khach_moi_kiotviet', 'wf_sheet_lead']
function mauLuong(): WorkflowV2[] {
  return LUONG_CO_SAN
    .map((id) => MAU_WORKFLOW.find((m) => m.id === id))
    .filter((m): m is MauWorkflow => !!m)
    .map((m) => ({ ...luongTuMau(m), enabled: true }))
}

function mauAnhXa(): Mapping[] {
  return [
    KIOTVIET_DEFAULT_MAPPING,
    {
      id: 'map_kiotviet_customer', source: 'kiotviet', name: 'KiotViet khách hàng → CRM',
      rules: [
        { from: 'Code', to: 'customer.code', transform: 'trim' },
        { from: 'Name', to: 'customer.name', transform: 'trim' },
        { from: 'ContactNumber', to: 'customer.phone', transform: 'phone_vn' },
        { from: 'Gender', to: 'customer.gender', transform: 'none' },
        { from: 'BirthDate', to: 'customer.birthday', transform: 'date' },
        { from: 'Address', to: 'customer.address', transform: 'trim' },
      ],
    },
    {
      id: 'map_gsheet_khach', source: 'gsheet', name: 'Sheet "Khách quan tâm" → CRM',
      rules: [
        { from: 'Họ tên', to: 'customer.name', transform: 'trim' },
        { from: 'SĐT', to: 'customer.phone', transform: 'phone_vn' },
        { from: 'Nhu cầu', to: 'customer.nhu_cau', transform: 'trim' },
        { from: 'Nguồn', to: 'customer.nguon', transform: 'trim' },
      ],
    },
  ]
}


let state: StoreV2 = { workflows: [], connectors: [], mappings: [], runs: [], daTai: false, dangTai: false }
const listeners = new Set<() => void>()
function dat(patch: Partial<StoreV2>) { state = { ...state, ...patch }; listeners.forEach((l) => l()) }
function baoLoi(err: unknown, mo = 'Không lưu được'): never { toast.error(`${mo}: ${apiError(err)}`); throw err }

const B = '/automation-v2'
let daNapMau = false
let hangDoiTai: Promise<void> | null = null

async function taiTatCa(): Promise<void> {
  if (hangDoiTai) return hangDoiTai
  hangDoiTai = (async () => {
    dat({ dangTai: true })
    try {
      const [w, c, m, r] = await Promise.all([
        api.get<WorkflowV2[]>(`${B}/workflows`), api.get<Connector[]>(`${B}/connectors`),
        api.get<Mapping[]>(`${B}/mappings`), api.get<WorkflowRun[]>(`${B}/runs`, { params: { limit: 300 } }),
      ])
      // Tổ chức mới tinh: nạp sẵn luồng mẫu (đang tắt) + ánh xạ mặc định, một lần.
      if (!w.data.length && !m.data.length && !daNapMau) {
        daNapMau = true
        await api.post(`${B}/seed`, { workflows: mauLuong(), mappings: mauAnhXa() }).catch(() => {})
        hangDoiTai = null
        return taiTatCa()
      }
      dat({ workflows: w.data, connectors: c.data, mappings: m.data, runs: r.data, daTai: true })
    } catch (err) {
      toast.error(`Không tải được dữ liệu tự động hoá: ${apiError(err)}`)
      dat({ daTai: true })
    } finally {
      dat({ dangTai: false })
      hangDoiTai = null
    }
  })()
  return hangDoiTai
}

async function taiLuot(): Promise<void> {
  try { dat({ runs: (await api.get<WorkflowRun[]>(`${B}/runs`, { params: { limit: 300 } })).data }) } catch { /* bỏ qua lần này */ }
}

/** Dùng trong mọi màn Tự động hoá v2: tự tải lần đầu + làm mới nhật ký mỗi 20 giây. */
export function useStoreV2(): StoreV2 {
  const s = useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l) }, () => state)
  useEffect(() => {
    if (!state.daTai && !state.dangTai) void taiTatCa()
    const t = setInterval(() => { if (document.visibilityState === 'visible') void taiLuot() }, 20_000)
    return () => clearInterval(t)
  }, [])
  return s
}

const thay = <T extends { id: string }>(ds: T[], x: T) => (ds.some((d) => d.id === x.id) ? ds.map((d) => (d.id === x.id ? x : d)) : [x, ...ds])

export const storeV2 = {
  get: () => state,
  taiLai: taiTatCa,
  taiLaiLuot: taiLuot,

  /** Nạp lại luồng mẫu còn thiếu (không đụng luồng đã có). */
  reset: async () => {
    try {
      const { data } = await api.post<{ soLuong: number; soAnhXa: number }>(`${B}/seed`, { workflows: mauLuong(), mappings: mauAnhXa() })
      await taiTatCa()
      toast.success(data.soLuong || data.soAnhXa ? `Đã nạp ${data.soLuong} luồng mẫu, ${data.soAnhXa} bảng ánh xạ` : 'Đã có đủ luồng mẫu')
    } catch (err) { baoLoi(err, 'Không nạp được mẫu') }
  },
  importJson: async (s: Partial<StoreV2>) => {
    try {
      await api.post(`${B}/seed`, { workflows: (s.workflows ?? []).map((w) => ({ ...w, id: w.templateKey || w.id })), mappings: s.mappings ?? [] })
      await taiTatCa()
    } catch (err) { baoLoi(err, 'Không nhập được') }
  },

  saveWorkflow: async (wf: WorkflowV2) => {
    dat({ workflows: thay(state.workflows, wf) })
    try {
      const { data } = await api.put<WorkflowV2>(`${B}/workflows/${wf.id}`, { name: wf.name, description: wf.description, nodes: wf.nodes, edges: wf.edges, enabled: wf.enabled })
      dat({ workflows: thay(state.workflows, data) })
      return data
    } catch (err) { baoLoi(err) }
  },
  newWorkflow: async (name: string): Promise<WorkflowV2> => {
    try {
      const { data } = await api.post<WorkflowV2>(`${B}/workflows`, { name })
      dat({ workflows: [data, ...state.workflows] })
      return data
    } catch (err) { return baoLoi(err, 'Không tạo được luồng') }
  },
  duplicateWorkflow: async (id: string) => {
    try { const { data } = await api.post<WorkflowV2>(`${B}/workflows/${id}/duplicate`); dat({ workflows: [data, ...state.workflows] }) } catch (err) { baoLoi(err) }
  },
  deleteWorkflow: async (id: string) => {
    try { await api.delete(`${B}/workflows/${id}`); dat({ workflows: state.workflows.filter((w) => w.id !== id), runs: state.runs.filter((r) => r.workflowId !== id) }) } catch (err) { baoLoi(err, 'Không xoá được') }
  },
  toggleWorkflow: async (id: string, enabled: boolean) => {
    dat({ workflows: state.workflows.map((w) => (w.id === id ? { ...w, enabled } : w)) })
    try { const { data } = await api.post<WorkflowV2>(`${B}/workflows/${id}/toggle`, { enabled }); dat({ workflows: thay(state.workflows, data) }) } catch (err) {
      dat({ workflows: state.workflows.map((w) => (w.id === id ? { ...w, enabled: !enabled } : w)) }); baoLoi(err, 'Không đổi được trạng thái')
    }
  },
  /** Chạy thật luồng "Thủ công" cho một khách. */
  runManual: async (id: string, contactId: string) => {
    const { data } = await api.post<WorkflowRun>(`${B}/workflows/${id}/run`, { contactId })
    dat({ runs: [data, ...state.runs] })
    return data
  },

  saveConnector: async (c: Connector) => {
    try {
      const { data } = await api.put<Connector>(`${B}/connectors/${c.id}`, { name: c.name, config: c.config, status: c.status })
      dat({ connectors: thay(state.connectors, data) })
      return data
    } catch (err) { baoLoi(err) }
  },
  newConnector: async (type: Connector['type'], name: string): Promise<Connector> => {
    try {
      const { data } = await api.post<Connector>(`${B}/connectors`, { type, name })
      dat({ connectors: [...state.connectors, data] })
      return data
    } catch (err) { return baoLoi(err, 'Không tạo được nguồn') }
  },
  deleteConnector: async (id: string) => {
    try { await api.delete(`${B}/connectors/${id}`); dat({ connectors: state.connectors.filter((x) => x.id !== id) }) } catch (err) { baoLoi(err, 'Không xoá được') }
  },
  capNhatConnector: (c: Connector) => dat({ connectors: thay(state.connectors, c) }),

  saveMapping: async (m: Mapping) => {
    const co = state.mappings.some((x) => x.id === m.id)
    dat({ mappings: co ? state.mappings.map((x) => (x.id === m.id ? m : x)) : [...state.mappings, m] })
    try {
      const { data } = co
        ? await api.put<Mapping>(`${B}/mappings/${m.id}`, { name: m.name, rules: m.rules })
        : await api.post<Mapping>(`${B}/mappings`, m)
      dat({ mappings: state.mappings.map((x) => (x.id === m.id ? data : x)) })
      return data
    } catch (err) { baoLoi(err) }
  },

  /** Lưu lượt "Chạy thử" (mô phỏng trên trình duyệt) vào nhật ký máy chủ. */
  addRun: async (run: WorkflowRun) => {
    dat({ runs: [run, ...state.runs] })
    try {
      const { data } = await api.post<WorkflowRun>(`${B}/runs`, { workflowId: run.workflowId, sampleId: run.sampleId, sampleLabel: run.sampleLabel, status: run.status, steps: run.steps, context: run.context, durationMs: run.durationMs })
      dat({ runs: state.runs.map((r) => (r.id === run.id ? data : r)) })
    } catch { /* nhật ký chạy thử không quan trọng bằng thao tác chính */ }
  },
  clearRuns: async (workflowId?: string) => {
    try {
      await api.delete(`${B}/runs`, { params: workflowId ? { workflowId } : {} })
      dat({ runs: state.runs.filter((r) => (workflowId ? r.workflowId !== workflowId : false) || r.status === 'waiting' || r.status === 'running') })
    } catch (err) { baoLoi(err, 'Không xoá được nhật ký') }
  },
}
